import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, Typography, Tag, Tabs } from 'antd';

import {
  ANALYZED_METRICS,
  PlayerAnalysisType,
  selectors,
  PTF_RETURN_ANALYZED,
  SHARPE_ANALYZED,
  ALPHA_ANALYZED,
  MAX_DD_ANALYZED,
  VAR_ANALYZED,
  AVG_LEV_ANALYZED,
  MAX_LEV_ANALYZED,
  LIMIT_BREAKING_ANALYZED,
  ORDERS_ANALYZED,
  AVG_SLIPPAGE,
  AVG_ACCURACY,
  AVG_HOLDING_PERIOD,
  CURRENT_LEVERAGE,
  VOL_ANALYZED,
} from 'state';
import { formatters } from 'services';
import PlayerPnLDistributionTab from './PlayerPnLDistributionTabApex';
import PlayerPnLEvolutionTab from './PlayerPnLEvolutionTab';
import PlayerConcentrationTab from './PlayerConcentrationTabApex';
import PlayerAIReportTab from './PlayerAIReportTab';

export type Props = {
  player: PlayerAnalysisType;
};

const { Text, Paragraph } = Typography;

const metricRenderers: Record<
  number,
  (value: number | null | undefined) => React.ReactNode
> = {
  [PTF_RETURN_ANALYZED]: v => formatters.percent2Digit(v ?? 0),
  [SHARPE_ANALYZED]: v => formatters.commasExact1Digit(v ?? 0),
  [ALPHA_ANALYZED]: v => formatters.commasExact1Digit(v ?? 0),
  [MAX_DD_ANALYZED]: v => formatters.percent2Digit(v ?? 0),
  [VAR_ANALYZED]: v => formatters.millionNoUnitExact2Digits(v ?? 0),
  [AVG_LEV_ANALYZED]: v => formatters.commasExact1Digit(v ?? 0),
  [MAX_LEV_ANALYZED]: v => formatters.commasExact1Digit(v ?? 0),
  [LIMIT_BREAKING_ANALYZED]: v => formatters.millionNoUnitExact2Digits(v ?? 0),
  [ORDERS_ANALYZED]: v => formatters.commasNoDigits(v ?? 0),
  [AVG_SLIPPAGE]: v => formatters.percent2Digit(v ?? 0),
  [AVG_ACCURACY]: v => formatters.percent2Digit(v ?? 0),
  [AVG_HOLDING_PERIOD]: v => formatters.commasExact1Digit(v ?? 0),
  [CURRENT_LEVERAGE]: v => formatters.commasExact1Digit(v ?? 0),
  [VOL_ANALYZED]: v => formatters.percent2Digit(v ?? 0),
};

const bandColors: Record<string, string> = {
  A: 'green',
  B: 'blue',
  C: 'orange',
  D: 'red',
};

const PlayerAnalysisCard: React.FC<Props> = ({ player }) => {
  const session = useSelector(selectors.activeSession);
  useSelector(selectors.currencyById(session.reporting_currency));

  const [activeTab, setActiveTab] = useState<
    | 'analysis'
    | 'ai_report'
    | 'pnl_distribution'
    | 'pnl_evolution'
    | 'concentration'
  >('analysis');
  const explainedMetrics = useMemo(
    () =>
      player.analyzed_metrics.filter(m => m.key_characteristic_text != null),
    [player.analyzed_metrics]
  );

  const renderMetricsSidebar = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: 170,
      }}
    >
      {explainedMetrics.map(metric => {
        const label =
          ANALYZED_METRICS[metric.analyzed_metric] ??
          `Metric ${metric.analyzed_metric}`;

        const renderValue =
          metricRenderers[metric.analyzed_metric] ??
          ((v: number | null | undefined) => (v != null ? v.toFixed(2) : '-'));

        return (
          <div
            key={metric.analyzed_metric}
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
            }}
          >
            <div style={{ width: 130 }}>
              <Text strong>{label}</Text>
              <div>
                <Text type="secondary">{renderValue(metric.value)}</Text>
              </div>
            </div>

            <div style={{ width: 25 }}>
              {metric.band && (
                <Tag color={bandColors[metric.band] ?? 'default'}>
                  {metric.band}
                </Tag>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderNonAnalysisTab = (
    content: React.ReactNode,
    centered = true,
    constrainToRowHeight = false
  ) => {
    if (explainedMetrics.length === 0) {
      return (
        <div
          style={
            centered
              ? {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  minHeight: 200,
                }
              : {
                  width: '100%',
                  minHeight: 0,
                  overflow: 'hidden',
                }
          }
        >
          {content}
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '190px minmax(0, 1fr)',
          columnGap: 6,
          alignItems: 'stretch',
        }}
      >
        {renderMetricsSidebar()}
        <div
          style={
            centered
              ? {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }
              : {
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  overflow: 'hidden',
                  position: constrainToRowHeight ? 'relative' : 'static',
                }
          }
        >
          {constrainToRowHeight ? (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {content}
            </div>
          ) : (
            content
          )}
        </div>
      </div>
    );
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 16, width: 800 }}
      title={player.player_name}
      extra={
        <Tabs
          size="small"
          activeKey={activeTab}
          onChange={key =>
            setActiveTab(
              key as
                | 'analysis'
                | 'ai_report'
                | 'pnl_distribution'
                | 'pnl_evolution'
                | 'concentration'
            )
          }
          style={{ marginBottom: 0 }}
        >
          <Tabs.TabPane tab="Overview" key="analysis" />
          <Tabs.TabPane tab="Report" key="ai_report" />
          <Tabs.TabPane tab="PnL Distribution" key="pnl_distribution" />
          <Tabs.TabPane tab="Concentration" key="concentration" />
          <Tabs.TabPane tab="PnL Evolution" key="pnl_evolution" />
        </Tabs>
      }
    >
      {activeTab === 'analysis' ? (
        explainedMetrics.length === 0 ? (
          <Text type="secondary">
            No analysis available yet for this player.
          </Text>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '190px minmax(0, 1fr)',
              columnGap: 6,
              alignItems: 'stretch',
            }}
          >
            {renderMetricsSidebar()}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                height: '100%',
              }}
            >
              {explainedMetrics.map(metric => (
                <Paragraph
                  key={metric.analyzed_metric}
                  style={{ marginBottom: 0, height: 44 }}
                >
                  {metric.key_characteristic_text}
                </Paragraph>
              ))}
            </div>
          </div>
        )
      ) : (
        <div style={{ width: '100%' }}>
          {activeTab === 'ai_report' &&
            renderNonAnalysisTab(
              <PlayerAIReportTab playerId={player.player_id} />,
              false,
              true
            )}
          {activeTab === 'pnl_distribution' &&
            renderNonAnalysisTab(<PlayerPnLDistributionTab player={player} />)}
          {activeTab === 'pnl_evolution' &&
            renderNonAnalysisTab(<PlayerPnLEvolutionTab player={player} />)}
          {activeTab === 'concentration' &&
            renderNonAnalysisTab(<PlayerConcentrationTab player={player} />)}
        </div>
      )}
    </Card>
  );
};

export default PlayerAnalysisCard;
